import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const BOOKING_CHARGE = 1000;

const CartPage = () => {
  const [cart, setCart] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [productMap, setProductMap] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const navigate = useNavigate();
  const apiBaseUrl = (() => {
    const raw =
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND ||
      "http://localhost:8000";
    const trimmed = String(raw).replace(/\/$/, "");
    return /\/api\/v1$/i.test(trimmed) ? trimmed : `${trimmed}/api/v1`;
  })();
  const assetBaseUrl = apiBaseUrl.replace(/\/api\/v1$/i, "");

  useEffect(() => {
    const loadCartAndStock = async () => {
      const data =
        JSON.parse(localStorage.getItem("cart")) || [];
      setCart(data);

      const stockObj = {};
      const productObj = {};

      for (let item of data) {
        if (item.item_type === "banner") {
          stockObj[item.id] =
            item.stock_quantity ?? item.stock ?? 0;
          continue;
        }
        try {
          const res = await fetch(
            `${apiBaseUrl}/products/one/${item.id}`
          );
          const result = await res.json();

          if (result?.product) {
            productObj[item.id] = result.product;
          }
          stockObj[item.id] =
            result.product?.stock_quantity ?? 0;
        } catch (error) {
          stockObj[item.id] = 0;
        }
      }

      setStockMap(stockObj);
      setProductMap(productObj);
    };

    loadCartAndStock();
  }, []);

  const showMessage = (msg) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2200);
  };

  const updateStorage = (updated) => {
    localStorage.setItem(
      "cart",
      JSON.stringify(updated)
    );
    setCart(updated);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const increaseQty = (id) => {
    const updated = cart.map((item) => {
      if (item.id === id) {
        const availableStock =
          stockMap[item.id] ?? 0;

        if (item.quantity >= availableStock) {
          showMessage(
            "No more stock available for this product"
          );
          return item;
        }

        showMessage("Booking quantity increased");
        return {
          ...item,
          quantity: item.quantity + 1,
        };
      }
      return item;
    });

    updateStorage(updated);
  };

  const decreaseQty = (id) => {
    const updated = cart
      .map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: item.quantity - 1,
            }
          : item
      )
      .filter((item) => item.quantity > 0);

    updateStorage(updated);
    showMessage("Booking quantity decreased");
  };

  const removeItem = (id) => {
    const updated = cart.filter(
      (item) => item.id !== id
    );
    updateStorage(updated);
    showMessage("Product removed from booking");
  };

  const clearCart = () => {
    updateStorage([]);
    showMessage("All bookings cleared");
  };

  const totalBookingAmount = cart.reduce(
    (sum, item) =>
      sum + BOOKING_CHARGE * item.quantity,
    0
  );

  const totalQty = cart.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const getOriginalPrice = (item) => {
    const fallback = productMap[item?.id];
    const price = Number(
      item.selling_price ??
        item.original_price ??
        item.price ??
        item.sprice ??
        item.mrp ??
        fallback?.selling_price ??
        fallback?.original_price ??
        fallback?.original_price ??
        fallback?.price ??
        fallback?.sprice ??
        fallback?.mrp
    );
    return Number.isFinite(price) ? price : 0;
  };

  const normalizeImage = (value) => {
    if (!value) return "";
    const raw = String(value);
    if (/^https?:\/\//i.test(raw)) return raw;
    const cleaned = raw.replace(/^\/+/, "");
    return `${assetBaseUrl}/uploads/${cleaned}`;
  };

  const getItemImage = (item) => {
    if (item?.item_type === "banner") {
      if (!item?.image) return "";
      const raw = String(item.image);
      if (/^https?:\/\//i.test(raw)) return raw;
      return `${assetBaseUrl}/banner/${raw.replace(/^\/+/, "")}`;
    }

    const fallback = productMap[item?.id];
    let images = [];
    try {
      if (Array.isArray(item?.images)) {
        images = item.images;
      } else {
        images = item?.images ? JSON.parse(item.images) : [];
      }
    } catch {
      images = [];
    }

    if (images.length === 0 && fallback) {
      try {
        if (Array.isArray(fallback?.images)) {
          images = fallback.images;
        } else {
          images = fallback?.images ? JSON.parse(fallback.images) : [];
        }
      } catch {
        images = [];
      }
    }

    if (images.length > 0) {
      return normalizeImage(images[0]);
    }

    const singleImage =
      item?.image ||
      item?.product_image ||
      item?.productImage ||
      item?.thumbnail ||
      fallback?.image ||
      fallback?.product_image ||
      fallback?.productImage ||
      fallback?.thumbnail;
    if (singleImage) return normalizeImage(singleImage);

    return "";
  };

  const totalOriginalAmount = cart.reduce(
    (sum, item) =>
      sum + getOriginalPrice(item) * item.quantity,
    0
  );

  const totalBalanceAmount = totalOriginalAmount + totalBookingAmount;

  const checkoutBooking = () => {
    if (cart.length === 0) {
      showMessage(
        "Your booking cart is empty"
      );
      return;
    }

    for (let item of cart) {
      if (
        (stockMap[item.id] || 0) <
        item.quantity
      ) {
        showMessage(
          `Not enough stock for ${item.name}`
        );
        return;
      }
    }

    console.log("Redirecting to checkout...");
    navigate("/checkout");
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* TOP BAR */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3 text-gray-600">
            <Link
              to="/"
              className="w-8 h-8 flex items-center justify-center rounded-full border border-[#1F3C88] text-[#1F3C88]"
            >
              {"←"}
            </Link>
            <span>Home</span>
            <span>/</span>
            <span className="font-semibold text-black">
              Booking Cart
            </span>
          </div>
          <button
            onClick={clearCart}
            className="text-sm text-red-500 hover:underline"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8">
          {/* LEFT: CART ITEMS */}
          <div className="space-y-6">
            {cart.length === 0 && (
              <div className="bg-white rounded-2xl p-8 border text-center text-gray-500">
                Your booking cart is empty.
              </div>
            )}

            {cart.map((item) => {
              const imageSrc = getItemImage(item);
              const originalPrice = getOriginalPrice(item);
              const deliveryBalance = originalPrice + BOOKING_CHARGE;

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-5 border flex flex-col sm:flex-row gap-4"
                >
                  <div className="w-28 h-28 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">No Image</span>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="font-semibold text-[#1F3C88]">
                          {item.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Only {(stockMap[item.id] ?? 0) || "N/A"} left in stock
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <div className="flex items-center border rounded-xl overflow-hidden">
                        <button
                          onClick={() => decreaseQty(item.id)}
                          className="px-3 py-1 text-[#1F3C88]"
                        >
                          -
                        </button>
                        <span className="px-3 py-1 text-sm">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => increaseQty(item.id)}
                          className="px-3 py-1 text-[#1F3C88]"
                        >
                          +
                        </button>
                      </div>

                      <span className="text-sm text-gray-600">
                        Original: {"₹"}{originalPrice.toLocaleString()}
                      </span>
                      {/* <span className="text-sm text-gray-600">
                        Total: {"₹"}{deliveryBalance.toLocaleString()}
                      </span> */}

                      <span className="ml-auto text-base font-semibold text-[#E63946]">
                        Token Amount {"₹"}{BOOKING_CHARGE.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: SUMMARY */}
          <div className="bg-white rounded-2xl p-6 shadow border border-[#e3e9ff] h-fit">
            <h3 className="text-lg font-bold text-[#1F3C88] mb-4">
              Booking Summary
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span>Token Amount ({"₹"}1000 x {totalQty})</span>
                <span>
                  {"₹"}{totalBookingAmount.toLocaleString()}
                </span>
              </div>

              <hr />

              <div className="flex justify-between">
                <span>Original Price</span>
                <span>
                  {"₹"}{totalOriginalAmount.toLocaleString()}
                </span>
              </div>

              {/* <div className="flex justify-between">
                <span>Total Price (Original + Token)</span>
                <span>
                  {"₹"}{totalBalanceAmount.toLocaleString()}
                </span>
              </div> */}

              {/* <div className="flex justify-between text-xs text-gray-600">
                <span>Pay Now</span>
                <span>
                  {"₹"}{totalOriginalAmount.toLocaleString()}
                </span>
              </div> */}

              <div className="flex justify-between font-bold text-base">
                <span>Total Price</span>
                <span className="text-[#E63946]">
                  {"₹"}{totalBalanceAmount.toLocaleString()}
                </span>
              </div>
            </div>

            <button
              onClick={checkoutBooking}
              className="w-full mt-6 bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc] text-white py-3 rounded-xl font-semibold text-sm hover:opacity-95"
            >
              Pay Now
            </button>
          </div>
        </div>

        {/* TOAST */}
        {showToast && (
          <div className="fixed bottom-6 right-6 z-50">
            <div className="bg-gradient-to-r from-[#1F3C88] to-[#E63946] text-white px-5 py-3 rounded-xl shadow-lg">
              <span className="text-sm font-medium">
                {toastMsg}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-10">
        <div className="rounded-xl border border-[#e3e9ff] bg-[#fff9ee] p-4 text-xs text-gray-700 leading-relaxed flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p>
            <span className="font-semibold text-[#1F3C88]">Note:</span>{" "}

            A standard booking token amount of ₹1000 is applicable on all orders. This amount is mandatory and will be charged at the time of order placement. The token amount will be adjusted against the total order value.

            Please note that the ₹1000 token amount is non-refundable under all circumstances. This policy is implemented to ensure genuine orders and maintain a smooth and reliable purchasing experience for all customers.

            We appreciate your understanding and trust in choosing our services.
           <br/>

<strong>For more information, please contact PC Care Computer:</strong><br/>

📞 <a href="tel:+919238264606" style={{ textDecoration: "none", color: "#000" }}>
+91 92382 64606
</a>


          </p>
        
        </div>
      </div>

    </>

  );
};

export default CartPage;




